// Ação coletiva — o que o classificador viu nos autos, o que o produto fez, e onde os
// dois brigam. Fonte: GET /coletiva (só leitura; a tabela é gravada pelo pipeline).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { Link } from '@tanstack/react-router';
import { Users, AlertTriangle, ChevronRight } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';
import { formatRangeShort, useDateRange, useDateRangeKey } from '@/lib/dateRange';

// ----------------------------------------------------------------------------- tipos
interface Trecho { tipo: string; trecho: string }
interface ClasseKpi {
  classe: string; label: string; legenda: string;
  n: number; entregues: number; guard: number; cobrados_sem_estorno: number; alta_confianca: number;
}
interface Conflito {
  processo_id: number; tipo: string; severidade: 'alta' | 'media'; motivo: string;
  classe: string; confianca: string | null; status: string | null;
  org_id: number | null; org_nome: string; entregue: boolean; cobrado: boolean; estornado: boolean;
}
interface Caso {
  processo_id: number; org_id: number | null; org_nome: string; created_at: string | null;
  status: string | null; status_message: string | null; modo_calculo: string | null; filename: string | null;
  classe: string; confianca: string | null; polo_ativo: string | null; polo_doc_digitos: number | null;
  n_substituidos: number | null; substituido_nome: string | null;
  entregue: boolean; cobrado: boolean; estornado: boolean; guard_disparou: boolean;
  motivo: string | null; trechos: Trecho[]; contagens: Record<string, number>;
  origem: string | null; versao: string | null;
}
interface ColetivaData {
  window: { start: string; end: string; days: number };
  as_of: string;
  shadow: 'ON' | 'OFF';
  filtro_classe: string | null;
  kpis: { total_avaliados: number; coletivas: number; guard_disparou: number; por_classe: ClasseKpi[]; classes_visiveis: string[] };
  conflitos: Conflito[];
  casos: Caso[];
  legendas: Record<string, string>;
  labels: Record<string, string>;
}

// ----------------------------------------------------------------------------- utils
const fmtInt = (n: number | null | undefined) => (n == null ? '—' : new Intl.NumberFormat('pt-BR').format(n));
const fmtDia = (s: string | null) => (s ? new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—');

const CLASSE_COLOR: Record<string, string> = {
  coletiva_pura: 'red',
  coletiva_multi: 'amber',
  coletiva_individual: 'blue',
  mencao_fraca: 'gray',
  nao_coletiva: 'gray',
};

const CONF_COLOR: Record<string, string> = { alta: 'emerald', media: 'amber', baixa: 'gray' };

const TIPO_LABEL: Record<string, string> = {
  entregue_inliquidavel: 'Entregamos o inliquidável',
  retido_com_individual: 'Retivemos quem tinha dados',
  polo_vazio: 'Extração cega (polo vazio)',
  multi_sem_caminho: 'Multi sem caminho de produto',
};

// ----------------------------------------------------------------------------- componentes
function ClasseCard({ k, ativo, onClick }: { k: ClasseKpi; ativo: boolean; onClick: () => void }) {
  const cor = CLASSE_COLOR[k.classe] || 'gray';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-3 py-2 text-left transition ${
        ativo
          ? 'border-gray-900 dark:border-gray-100 bg-gray-50 dark:bg-gray-800'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-400 dark:hover:border-gray-500'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{k.label}</span>
        <Badge size="xs" color={cor}>{k.classe}</Badge>
      </div>
      <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-50">{fmtInt(k.n)}</div>
      <div className="text-[12px] leading-snug text-gray-700 dark:text-gray-200">{k.legenda}</div>
      <div className="mt-1 text-[12px] tabular-nums text-gray-700 dark:text-gray-200">
        {fmtInt(k.entregues)} entregues · {fmtInt(k.guard)} retidos · {fmtInt(k.cobrados_sem_estorno)} cobrados
      </div>
    </button>
  );
}

function Flag({ on, label, tone = 'gray' }: { on: boolean; label: string; tone?: string }) {
  if (!on) return null;
  return <Badge size="xs" color={tone}>{label}</Badge>;
}

// ----------------------------------------------------------------------------- página
export function ColetivaPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [classe, setClasse] = useState<string | null>(null);
  const [aberto, setAberto] = useState<number | null>(null);

  const q = useQuery<ColetivaData>({
    queryKey: ['coletiva', rangeKey],
    queryFn: async () => (await api().get('/coletiva', { params: apiParams })).data,
    staleTime: 5 * 60_000,
  });

  const casos = useMemo(() => {
    const todos = q.data?.casos ?? [];
    return classe ? todos.filter((c) => c.classe === classe) : todos;
  }, [q.data, classe]);

  if (q.isLoading) return <LoadingSkeleton />;
  if (q.isError || !q.data)
    return (
      <Card>
        <Text className="text-red-600 dark:text-red-400">
          Erro ao carregar ação coletiva: {extractErrorMessage(q.error, 'falha na consulta')}
        </Text>
      </Card>
    );
  const d = q.data;
  const visiveis = d.kpis.por_classe.filter((k) => d.kpis.classes_visiveis.includes(k.classe));
  const sombra = d.shadow === 'OFF';
  const altas = d.conflitos.filter((c) => c.severidade === 'alta');
  const medias = d.conflitos.filter((c) => c.severidade !== 'alta');

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="max-w-3xl">
          <Title>Ação coletiva</Title>
          <Text>
            Ação coletiva é o sindicato agindo como substituto processual: a condenação é dos substituídos, não do
            sindicato que consta no polo ativo.
          </Text>
          <Text>
            Importa porque sem contrato de trabalho individual nos autos não há o que liquidar — entregar um cálculo
            nesse caso é entregar um número inventado. Janela: {formatRangeShort(range)} ({d.window.days} dias). Orgs
            internas (3 e 4) excluídas.
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={sombra ? 'amber' : 'emerald'} icon={Users}>
            {sombra ? 'modo sombra — guard só observa' : 'guard ativo — retém'}
          </Badge>
          <Badge color="gray">{fmtInt(d.kpis.total_avaliados)} avaliados</Badge>
        </div>
      </div>

      {/* KPIs por classe */}
      <Card>
        <Title className="text-base">Classes na janela</Title>
        <Text className="mb-3">
          {fmtInt(d.kpis.total_avaliados)} processos avaliados pelo classificador, dos quais {fmtInt(d.kpis.coletivas)}{' '}
          coletivos e {fmtInt(d.kpis.guard_disparou)} com o guard disparado. Clique numa classe para filtrar a tabela.
        </Text>
        <Grid numItemsSm={2} numItemsLg={4} className="gap-3">
          {visiveis.map((k) => (
            <ClasseCard
              key={k.classe}
              k={k}
              ativo={classe === k.classe}
              onClick={() => setClasse(classe === k.classe ? null : k.classe)}
            />
          ))}
        </Grid>
      </Card>

      {/* Conflitos */}
      <Card className={altas.length ? 'ring-1 ring-red-300 dark:ring-red-700' : ''}>
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-4 w-4 ${altas.length ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
          <Title className="text-base">Conflitos ({d.conflitos.length})</Title>
        </div>
        <Text className="mb-3">
          Onde o veredito do classificador e o que o produto fez não batem. Vermelho = já teve consequência para o
          cliente; âmbar = risco em aberto.
        </Text>
        {d.conflitos.length === 0 ? (
          <Text className="text-gray-700 dark:text-gray-200">Nenhum conflito na janela.</Text>
        ) : (
          <div className="space-y-2">
            {[...altas, ...medias].map((c, i) => (
              <div
                key={`${c.processo_id}-${c.tipo}-${i}`}
                className={`flex flex-wrap items-start gap-2 rounded-lg border px-3 py-2 text-[13px] leading-snug ${
                  c.severidade === 'alta'
                    ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950'
                    : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950'
                }`}
              >
                <Badge size="xs" color={c.severidade === 'alta' ? 'red' : 'amber'}>
                  {TIPO_LABEL[c.tipo] || c.tipo}
                </Badge>
                <Link
                  to="/cases/$caseId"
                  params={{ caseId: String(c.processo_id) }}
                  className="font-bold tabular-nums text-gray-900 underline decoration-dotted dark:text-gray-50"
                >
                  #{c.processo_id}
                </Link>
                <span className="text-gray-900 dark:text-gray-50">{c.motivo}</span>
                <span className="ml-auto whitespace-nowrap text-[12px] text-gray-700 dark:text-gray-200">
                  {c.org_nome} · {c.status || '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tabela de casos */}
      <Card>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <Title className="text-base">Casos ({fmtInt(casos.length)})</Title>
            <Text>Clique na linha para ver o motivo e os trechos que o classificador usou.</Text>
          </div>
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setClasse(null)}
              className={`rounded border px-2 py-1 text-[12px] font-medium ${
                classe === null
                  ? 'border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                  : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
              }`}
            >
              Todas
            </button>
            {d.kpis.por_classe.map((k) => (
              <button
                key={k.classe}
                type="button"
                onClick={() => setClasse(k.classe)}
                className={`rounded border px-2 py-1 text-[12px] font-medium ${
                  classe === k.classe
                    ? 'border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                    : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
                }`}
              >
                {k.label} ({k.n})
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-300 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:border-gray-600 dark:text-gray-200">
                <th className="py-1.5 pr-3">Caso</th>
                <th className="py-1.5 pr-3">Org</th>
                <th className="py-1.5 pr-3">Classe</th>
                <th className="py-1.5 pr-3">Polo ativo</th>
                <th className="py-1.5 pr-3 text-right">Subst.</th>
                <th className="py-1.5 pr-3">Status</th>
                <th className="py-1.5 pr-3">Sinais</th>
              </tr>
            </thead>
            <tbody>
              {casos.map((c) => {
                const open = aberto === c.processo_id;
                return (
                  <tr key={c.processo_id} className="border-b border-gray-100 align-top dark:border-gray-800">
                    <td colSpan={7} className="p-0">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setAberto(open ? null : c.processo_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setAberto(open ? null : c.processo_id);
                        }}
                        className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-2 py-1.5"
                      >
                        <ChevronRight
                          className={`mt-1 h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform dark:text-gray-400 ${open ? 'rotate-90' : ''}`}
                        />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <Link
                              to="/cases/$caseId"
                              params={{ caseId: String(c.processo_id) }}
                              onClick={(e) => e.stopPropagation()}
                              className="font-bold tabular-nums text-gray-900 underline decoration-dotted dark:text-gray-50"
                            >
                              #{c.processo_id}
                            </Link>
                            <span className="tabular-nums text-gray-700 dark:text-gray-200">{fmtDia(c.created_at)}</span>
                            <span className="truncate font-medium text-gray-900 dark:text-gray-50">{c.org_nome}</span>
                            <Badge size="xs" color={CLASSE_COLOR[c.classe] || 'gray'}>
                              {d.labels[c.classe] || c.classe}
                            </Badge>
                            {c.confianca && (
                              <Badge size="xs" color={CONF_COLOR[c.confianca] || 'gray'}>
                                conf. {c.confianca}
                              </Badge>
                            )}
                            <span className="truncate text-gray-700 dark:text-gray-200">
                              {c.polo_ativo || <i className="text-amber-700 dark:text-amber-400">polo não extraído</i>}
                              {c.polo_doc_digitos ? ` · ${c.polo_doc_digitos} díg.` : ''}
                            </span>
                            {c.n_substituidos != null && (
                              <span className="tabular-nums text-gray-700 dark:text-gray-200">
                                {c.n_substituidos} subst.
                              </span>
                            )}
                            {c.substituido_nome && (
                              <span className="truncate text-gray-700 dark:text-gray-200">{c.substituido_nome}</span>
                            )}
                            <span className="font-semibold text-gray-900 dark:text-gray-50">{c.status || '—'}</span>
                            <Flag on={c.guard_disparou} label="guard" tone="amber" />
                            <Flag on={c.entregue} label="entregue" tone="emerald" />
                            <Flag on={c.cobrado && !c.estornado} label="cobrado" tone="blue" />
                            <Flag on={c.estornado} label="estornado" tone="gray" />
                          </div>
                          {c.status_message && (
                            <div className="truncate text-[12px] text-gray-600 dark:text-gray-300">{c.status_message}</div>
                          )}
                          {open && (
                            <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                              <div className="text-[13px] text-gray-900 dark:text-gray-50">
                                <b>Motivo:</b> {c.motivo || '—'}
                              </div>
                              {c.trechos.length > 0 && (
                                <div className="space-y-1">
                                  {c.trechos.map((t, i) => (
                                    <div key={i} className="text-[12px] leading-snug text-gray-700 dark:text-gray-200">
                                      <span className="font-semibold uppercase tracking-wide">{t.tipo}</span>{' '}
                                      <span className="italic">“{t.trecho}”</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="text-[12px] text-gray-600 dark:text-gray-300">
                                origem {c.origem || '—'} · versão {c.versao || '—'} · modo {c.modo_calculo || '—'}
                                {c.filename ? ` · ${c.filename}` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {casos.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-3 text-gray-700 dark:text-gray-200">
                    Nenhum caso nessa classe na janela.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Text className="text-[12px] text-gray-600 dark:text-gray-300">
        Atualizado {new Date(d.as_of).toLocaleString('pt-BR')} · consulta ao vivo, sem cache no servidor · leitura
        apenas.
      </Text>
    </div>
  );
}
